/**
 * SEC-5 — monitor de inactividad coordinado entre pestañas.
 *
 * Las pestañas de un mismo navegador comparten la sesión de Supabase
 * (localStorage), pero los listeners de actividad son por ventana: una pestaña
 * ociosa cumplía 60 min sin eventos y cerraba la sesión de la pestaña donde el
 * usuario sí estaba trabajando. Ahora cada actividad escribe un timestamp
 * compartido, y al vencer su timer cada pestaña recalcula la inactividad con la
 * actividad MÁS RECIENTE entre la suya y la compartida: solo cierra si todas las
 * pestañas llevan `timeoutMs` sin actividad.
 *
 * Si localStorage no está disponible cae al comportamiento por pestaña.
 * Usa reloj de pared (Date.now): tras suspender el equipo, al despertar la
 * inactividad ya cuenta el tiempo dormido y la sesión se cierra.
 */
export const LAST_ACTIVITY_KEY = 'quipus_last_activity';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
const MIN_RECHECK_MS = 1000;

export interface InactivityMonitorOptions {
  timeoutMs:  number;
  warningMs:  number;
  onWarn:     () => void;
  onTimeout:  () => void;
  storage?:   Pick<Storage, 'getItem' | 'setItem'>;
  target?:    EventTarget;
  now?:       () => number;
  /** Máximo una escritura al storage compartido por este intervalo (scroll dispara muchos eventos). */
  writeThrottleMs?: number;
}

/** Devuelve la función que detiene el monitor. */
export function startInactivityMonitor(opts: InactivityMonitorOptions): () => void {
  const {
    timeoutMs, warningMs, onWarn, onTimeout,
    storage = localStorage,
    target = window,
    now = () => Date.now(),
    writeThrottleMs = 1000,
  } = opts;

  let lastLocal = now();
  let lastWrite = Number.NEGATIVE_INFINITY;
  let warned = false;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const readShared = (): number => {
    try {
      const n = Number(storage.getItem(LAST_ACTIVITY_KEY));
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const writeShared = (t: number, force = false): void => {
    if (!force && t - lastWrite < writeThrottleMs) return;
    lastWrite = t;
    try {
      storage.setItem(LAST_ACTIVITY_KEY, String(t));
    } catch {
      // sin storage compartido: se coordina solo por pestaña
    }
  };

  const idleMs = (): number => Math.max(0, now() - Math.max(lastLocal, readShared()));

  const clear = (): void => {
    if (timer) { clearTimeout(timer); timer = null; }
  };

  const tick = (): void => {
    timer = null;
    if (stopped) return;
    const idle = idleMs();

    if (idle >= timeoutMs) {
      stopped = true;
      detach();
      onTimeout();
      return;
    }

    const warnAt = timeoutMs - warningMs;
    if (idle >= warnAt) {
      if (!warned) { warned = true; onWarn(); }
      timer = setTimeout(tick, Math.max(timeoutMs - idle, MIN_RECHECK_MS));
    } else {
      warned = false; // otra pestaña volvió a estar activa
      timer = setTimeout(tick, Math.max(warnAt - idle, MIN_RECHECK_MS));
    }
  };

  const onActivity = (): void => {
    if (stopped) return;
    const t = now();
    lastLocal = t;
    writeShared(t);
    warned = false;
    clear();
    timer = setTimeout(tick, Math.max(timeoutMs - warningMs, MIN_RECHECK_MS));
  };

  const detach = (): void => {
    clear();
    for (const ev of ACTIVITY_EVENTS) target.removeEventListener(ev, onActivity);
  };

  for (const ev of ACTIVITY_EVENTS) target.addEventListener(ev, onActivity, { passive: true });
  writeShared(lastLocal, true);
  timer = setTimeout(tick, Math.max(timeoutMs - warningMs, MIN_RECHECK_MS));

  return () => {
    stopped = true;
    detach();
  };
}
